import { request as umiRequest, history } from '@umijs/max';
import type { RequestOptions } from '@umijs/max';
import { message } from 'antd';

/**
 * 后端响应格式
 * 后端统一返回格式：{ code: number, message: string, data: T }
 */
export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
  success: boolean;
}

/**
 * 请求配置选项
 */
export interface RequestOptionsWithSkipErrorHandler extends RequestOptions {
  skipErrorHandler?: boolean;
}

/**
 * 统一的网络请求封装
 * 
 * 功能特性：
 * 1. 自动添加 Bearer Token 到请求头
 * 2. 统一处理后端响应格式 { code, message, data }
 * 3. 自动处理 401 错误（清除 Token 并跳转登录）
 * 4. 提供类型安全的响应数据
 * 
 * @param url 请求地址
 * @param options 请求配置
 * @returns Promise<ApiResponse<T>> 返回后端响应格式
 * 
 * @example
 * ```ts
 * // GET 请求
 * const response = await request<API.AdminInfo>('/api/admin/info');
 * if (response.code === 200) {
 *   console.log(response.data); // 类型安全的 data
 * }
 * 
 * // POST 请求
 * const response = await request('/api/admin/login', {
 *   method: 'POST',
 *   data: { username: 'admin', password: '123456' }
 * });
 * if (response.code === 200) {
 *   localStorage.setItem('accessToken', response.data.accessToken);
 * }
 * ```
 */
export async function request<T = any>(
  url: string,
  options?: RequestOptionsWithSkipErrorHandler,
): Promise<ApiResponse<T>> {
  try {
    // 使用 umi 的 request 发送请求
    // umi 的拦截器已经处理了 Token 添加和响应格式转换
    const response = await umiRequest<{
      success: boolean;
      data: T;
      errorMessage?: string;
      errorCode?: number;
    }>(url, {
      ...options,
      skipErrorHandler: options?.skipErrorHandler || false,
    });

    // umi 的响应拦截器已经将后端格式 { code, message, data } 
    // 转换为 { success, data, errorMessage, errorCode }
    // 这里再转换回后端原始格式，方便统一使用
    if (response.success) {
      return {
        code: 200,
        message: response.errorMessage || 'success',
        success: true,
        data: response.data,
      } as ApiResponse<T>;
    } else {
      const errorCode = response.errorCode || 500;
      // Token 过期业务码
      if (errorCode === 401 || errorCode === 20005) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('permissions');
        const onLoginPage =
          window.location.pathname === '/user/login' ||
          window.location.pathname.endsWith('/user/login');
        if (!onLoginPage) {
          message.error(response.errorMessage || '登录已过期，请重新登录');
          history.push('/user/login');
        }
      }

      // 如果 skipErrorHandler 为 true，直接抛出错误
      if (options?.skipErrorHandler) {
        const error: any = new Error(response.errorMessage || '请求失败');
        error.code = errorCode;
        throw error;
      }
      
      // 返回错误响应
      return {
        code: errorCode,
        message: response.errorMessage || '请求失败',
        data: null as any,
        success: false,
      } as ApiResponse<T>;
    }
  } catch (error: any) {
    // 处理错误
    if (error.response) {
      const status = error.response.status;
      
      // 401 未授权：清 Token；已在登录页则不再跳转，避免死循环
      if (status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('permissions');
        const onLoginPage =
          window.location.pathname === '/user/login' ||
          window.location.pathname.endsWith('/user/login');
        if (!onLoginPage) {
          message.error('登录已过期，请重新登录');
          history.push('/user/login');
        }
      }
      
      // 如果 skipErrorHandler 为 true，直接抛出错误
      if (options?.skipErrorHandler) {
        throw error;
      }
      
      // 返回错误响应
      return {
        code: status,
        success: false,
        message: error.response.data?.message || `请求失败 (${status})`,
        data: null as any,
      } as ApiResponse<T>;
    }
    
    // 如果是业务错误（BizError），且 skipErrorHandler 为 true，直接抛出
    if (error.name === 'BizError' && options?.skipErrorHandler) {
      throw error;
    }
    
    // 其他错误，如果 skipErrorHandler 为 true，直接抛出
    if (options?.skipErrorHandler) {
      throw error;
    }
    
    // 网络错误或其他错误，返回错误响应
    return {
      code: 500,
      message: error.message || '网络错误，请稍后重试',
      data: null as any,
      success: false,
    } as ApiResponse<T>;
  }
}

/**
 * GET 请求快捷方法
 * 
 * @param url 请求地址
 * @param params 查询参数
 * @param options 请求配置
 * @returns Promise<ApiResponse<T>>
 * 
 * @example
 * ```ts
 * const response = await get<API.AdminInfo>('/api/admin/info', { id: 1 });
 * if (response.code === 200) {
 *   console.log(response.data);
 * }
 * ```
 */
export async function get<T = any>(
  url: string,
  params?: Record<string, any>,
  options?: RequestOptionsWithSkipErrorHandler,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    method: 'GET',
    ...(params ? { params } : {}),
    ...options,
  });
}

/**
 * POST 请求快捷方法
 * 
 * @param url 请求地址
 * @param data 请求体数据
 * @param options 请求配置
 * @returns Promise<ApiResponse<T>>
 * 
 * @example
 * ```ts
 * const response = await post('/api/admin/login', {
 *   username: 'admin',
 *   password: '123456'
 * });
 * if (response.code === 200) {
 *   console.log(response.data);
 * }
 * ```
 */
export async function post<T = any>(
  url: string,
  data?: any,
  options?: RequestOptionsWithSkipErrorHandler,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    method: 'POST',
    data,
    ...options,
  });
}

/**
 * PUT 请求快捷方法
 * 
 * @param url 请求地址
 * @param data 请求体数据
 * @param options 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export async function put<T = any>(
  url: string,
  data?: any,
  options?: RequestOptionsWithSkipErrorHandler,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    method: 'PUT',
    data,
    ...options,
  });
}

/**
 * DELETE 请求快捷方法
 * 
 * @param url 请求地址
 * @param options 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export async function del<T = any>(
  url: string,
  options?: RequestOptionsWithSkipErrorHandler,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    method: 'DELETE',
    ...options,
  });
}

/**
 * PATCH 请求快捷方法
 * 
 * @param url 请求地址
 * @param data 请求体数据
 * @param options 请求配置
 * @returns Promise<ApiResponse<T>>
 */
export async function patch<T = any>(
  url: string,
  data?: any,
  options?: RequestOptionsWithSkipErrorHandler,
): Promise<ApiResponse<T>> {
  return request<T>(url, {
    method: 'PATCH',
    data,
    ...options,
  });
}
